import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Instagram,
  HelpCircle, Search, ChevronRight, ArrowLeft, ListOrdered, X
} from 'lucide-react';
import logomark from 'assets/images/logos/Logomark.svg';
import { useLandingTheme } from '../hooks/useLandingTheme';
import helpData from 'data/help/helpData.json';
import LegalModal, { LegalDocKey } from './LegalModal';
import AboutModal from './AboutModal';

const CONTACT_EMAIL = 'contact@famousclicker.com';
const INSTAGRAM_URL = 'https://www.instagram.com/famous.clicker/';

interface Step {
  description: string;
  image: string;
  video: string;
}

interface Article {
  id: string;
  title: string;
  content: string;
  steps: Step[];
}

interface Category {
  id: string;
  title: string;
  icon: string;
  description: string;
  articles: Article[];
}

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const isDark = useLandingTheme();

  const [showHelpModal, setShowHelpModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeLegalDoc, setActiveLegalDoc] = useState<LegalDocKey | null>(null);
  const [showAboutModal, setShowAboutModal] = useState(false);

  const legalLinks: { key: LegalDocKey; labelKey: string }[] = [
    { key: 'termsOfUse', labelKey: 'landing.footer.legalLinks.termsOfUse' },
    { key: 'termsOfSale', labelKey: 'landing.footer.legalLinks.termsOfSale' },
    { key: 'privacyPolicy', labelKey: 'landing.footer.legalLinks.privacyPolicy' },
  ];

  const categories: Category[] = helpData.categories || [];

  const productLinks = [
    { key: 'features', href: '#features' },
    { key: 'pricing', href: '#pricing' },
  ];
  const socialLinks = [
    { icon: Instagram, label: 'Instagram', href: INSTAGRAM_URL },
  ];

  const handleOpenHelp = () => {
    setShowHelpModal(true);
    setSelectedCategory(null);
    setSelectedArticle(null);
    setSearchQuery('');
  };

  const handleCloseHelp = () => {
    setShowHelpModal(false);
    setSelectedCategory(null);
    setSelectedArticle(null);
    setSearchQuery('');
  };

  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category);
    setSelectedArticle(category.articles[0] || null);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedArticle(null);
  };

  const filteredCategories = categories.filter(category =>
    category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Theme tokens for the modal
  const modalBg = isDark ? '#0F172B' : '#FFFFFF';
  const modalBorder = isDark ? '#314158' : '#E2E8F0';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const cardBg = isDark ? '#0F172B' : '#FFFFFF';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const sectionBorder = isDark ? '#1D293D' : '#F1F5F9';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';
  const inputBg = isDark ? '#0F172B' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputText = isDark ? '#E2E8F0' : '#314158';
  const mutedText = isDark ? '#90A1B9' : '#62748E';
  const hoverBg = isDark ? '#1D293D' : '#F8FAFC';
  const activeItemBg = isDark ? '#162456' : '#EFF6FF';
  const activeItemBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const activeItemText = isDark ? '#51A2FF' : '#2B7FFF';
  const headerBg = isDark ? '#1D293D' : '#F8FAFC';
  const buttonBg = 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)';
  const stepBorderColor = isDark ? '#314158' : '#BFDBFE';

  return (
    <>
      <footer className="py-12 md:py-20 pb-8 md:pb-10" style={{ background: '#0F172B' }}>
        <div
          className="max-w-[1200px] mx-auto px-6 lg:px-0 flex flex-col items-center"
          style={{ gap: '40px' }}
        >
          {/* Main Content */}
          <div
            className="w-full grid grid-cols-2 md:grid-cols-4"
            style={{ gap: '32px' }}
          >
            {/* Logo & Tagline & Social */}
            <div className="col-span-2 md:col-span-1 flex flex-col" style={{ gap: '16px' }}>
              {/* Logo */}
              <div className="flex items-center" style={{ gap: '8px' }}>
                <img src={logomark} alt="" style={{ width: '32px', height: '32px' }} />
                <span
                  className="font-geist"
                  style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: 400 }}
                >
                  Famous<span style={{ fontWeight: 700 }}>Clicker</span>
                </span>
              </div>

              {/* Description */}
              <p
                className="font-geist"
                style={{
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#FFFFFF',
                  maxWidth: '270px',
                }}
              >
                {t('landing.footer.description')}
              </p>

              {/* Social Icons */}
              <div className="flex items-start" style={{ gap: '8px' }}>
                {socialLinks.map(({ icon: Icon, label, href }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex items-center justify-center"
                    style={{
                      width: '40px',
                      height: '40px',
                      background: '#314158',
                      borderRadius: '12px',
                    }}
                  >
                    <Icon size={24} color="#FFFFFF" />
                  </a>
                ))}
              </div>
            </div>

            {/* Product */}
            <div className="flex flex-col" style={{ gap: '16px' }}>
              <h4
                className="font-geist"
                style={{ fontWeight: 400, fontSize: '18px', lineHeight: '28px', color: '#FFFFFF' }}
              >
                {t('landing.footer.product')}
              </h4>
              <ul className="flex flex-col" style={{ gap: '8px' }}>
                {productLinks.map((link) => (
                  <li key={link.key}>
                    <a
                      href={link.href}
                      className="font-geist hover:text-white transition-colors"
                      style={{ fontWeight: 400, fontSize: '16px', lineHeight: '24px', color: '#90A1B9' }}
                    >
                      {t(`landing.footer.links.${link.key}`)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources — Help Center only */}
            <div className="flex flex-col" style={{ gap: '16px' }}>
              <h4
                className="font-geist"
                style={{ fontWeight: 400, fontSize: '18px', lineHeight: '28px', color: '#FFFFFF' }}
              >
                {t('landing.footer.resources')}
              </h4>
              <ul className="flex flex-col" style={{ gap: '8px' }}>
                <li>
                  <button
                    onClick={handleOpenHelp}
                    className="font-geist hover:text-white transition-colors"
                    style={{
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: '#90A1B9',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {t('landing.footer.links.helpCenter')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div className="flex flex-col" style={{ gap: '16px' }}>
              <h4
                className="font-geist"
                style={{ fontWeight: 400, fontSize: '18px', lineHeight: '28px', color: '#FFFFFF' }}
              >
                {t('landing.footer.company')}
              </h4>
              <ul className="flex flex-col" style={{ gap: '8px' }}>
                <li>
                  <button
                    onClick={() => setShowAboutModal(true)}
                    className="font-geist hover:text-white transition-colors"
                    style={{
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: '#90A1B9',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {t('landing.footer.links.aboutUs')}
                  </button>
                </li>
                <li>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="font-geist hover:text-white transition-colors"
                    style={{ fontWeight: 400, fontSize: '16px', lineHeight: '24px', color: '#90A1B9' }}
                  >
                    {t('landing.footer.links.contact')}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright Bar */}
          <div
            className="w-full flex flex-col items-center"
            style={{ borderTop: '1px solid #45556C', paddingTop: '24px', gap: '8px' }}
          >
            <p
              className="font-geist text-center text-xs md:text-sm"
              style={{ fontWeight: 400, lineHeight: '20px', color: '#90A1B9' }}
            >
              {t('landing.footer.copyright')}
            </p>
            <div
              className="flex flex-wrap items-center justify-center"
              style={{ gap: '8px 16px' }}
            >
              {legalLinks.map((link, idx) => (
                <React.Fragment key={link.key}>
                  {idx > 0 && (
                    <span style={{ color: '#45556C', fontSize: '12px' }}>|</span>
                  )}
                  <button
                    onClick={() => setActiveLegalDoc(link.key)}
                    className="font-geist hover:text-white transition-colors"
                    style={{
                      fontWeight: 400,
                      fontSize: '13px',
                      lineHeight: '20px',
                      color: '#90A1B9',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    {t(link.labelKey)}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* Help Center Modal */}
      {showHelpModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: isDark ? 'rgba(2, 6, 24, 0.7)' : 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCloseHelp();
          }}
        >
          <div
            style={{
              background: modalBg,
              border: `1px solid ${modalBorder}`,
              borderRadius: '16px',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: isDark
                ? '0 25px 50px rgba(2,6,24,0.5)'
                : '0 25px 50px rgba(0,0,0,0.15)',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 24px',
                borderBottom: `1px solid ${sectionBorder}`,
                background: headerBg,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '10px',
                  background: iconContainerBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <HelpCircle size={18} style={{ color: iconColor }} />
              </div>
              <span
                className="font-geist"
                style={{ fontSize: '16px', fontWeight: 600, color: titleColor, flex: 1 }}
              >
                {t('help.helpCenter')}
              </span>

              {/* Search */}
              <div style={{ position: 'relative', width: '220px', flexShrink: 0 }}>
                <input
                  type="text"
                  placeholder={t('help.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 10px 6px 30px',
                    fontSize: '13px',
                    border: `1px solid ${inputBorder}`,
                    borderRadius: '8px',
                    background: inputBg,
                    color: inputText,
                    outline: 'none',
                    boxSizing: 'border-box',
                    height: '32px',
                  }}
                />
                <Search
                  size={14}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: mutedText,
                  }}
                />
              </div>

              {/* Close */}
              <button
                onClick={handleCloseHelp}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: mutedText,
                  flexShrink: 0,
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div
              className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
              style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', minHeight: 0 }}
            >
              {!selectedCategory ? (
                /* Categories Grid */
                <>
                  {filteredCategories.length === 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '40px 16px',
                        background: cardBg,
                        border: `1px solid ${cardBorder}`,
                        borderRadius: '12px',
                      }}
                    >
                      <Search size={36} style={{ color: mutedText, marginBottom: '12px' }} />
                      <div style={{ fontSize: '14px', color: titleColor, marginBottom: '4px' }}>
                        {t('help.noArticlesFound')}
                      </div>
                      <div style={{ fontSize: '13px', color: mutedText }}>
                        {t('help.tryDifferentSearch')}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ gap: '12px' }}>
                      {filteredCategories.map((category) => (
                        <div
                          key={category.id}
                          onClick={() => handleCategoryClick(category)}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            padding: '16px',
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: '12px',
                            cursor: 'pointer',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = iconColor;
                            e.currentTarget.style.boxShadow = isDark
                              ? '0px 4px 12px rgba(43, 127, 255, 0.15)'
                              : '0px 4px 12px rgba(43, 127, 255, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = cardBorder;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              width: '32px',
                              height: '32px',
                              background: iconContainerBg,
                              borderRadius: '8px',
                            }}
                          >
                            <HelpCircle size={16} style={{ color: iconColor }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: titleColor, marginBottom: '3px' }}>
                              {category.title}
                            </div>
                            <div
                              style={{
                                fontSize: '12px',
                                color: mutedText,
                                marginBottom: '6px',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {category.description}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 500, color: iconColor }}>
                              <span>
                                {category.articles.length} {category.articles.length !== 1 ? t('help.articles') : t('help.article')}
                              </span>
                              <ChevronRight size={12} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                /* Article View */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Back Button */}
                  <button
                    onClick={handleBackToCategories}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: mutedText,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'color 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = iconColor)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = mutedText)}
                  >
                    <ArrowLeft size={16} />
                    {t('help.backToCategories')}
                  </button>

                  <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr]" style={{ gap: '12px' }}>
                    {/* Articles Sidebar */}
                    <div
                      style={{
                        background: cardBg,
                        border: `1px solid ${cardBorder}`,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        alignSelf: 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 14px',
                          borderBottom: `1px solid ${sectionBorder}`,
                        }}
                      >
                        <HelpCircle size={14} style={{ color: iconColor }} />
                        <span style={{ fontSize: '13px', fontWeight: 600, color: titleColor }}>
                          {selectedCategory.title}
                        </span>
                      </div>

                      <div style={{ padding: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {selectedCategory.articles.map((article) => {
                          const isActive = selectedArticle?.id === article.id;
                          return (
                            <button
                              key={article.id}
                              onClick={() => setSelectedArticle(article)}
                              style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: isActive ? 500 : 400,
                                background: isActive ? activeItemBg : 'transparent',
                                border: isActive ? `1px solid ${activeItemBorder}` : '1px solid transparent',
                                color: isActive ? activeItemText : titleColor,
                                cursor: 'pointer',
                                transition: 'background 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                if (!isActive) e.currentTarget.style.background = hoverBg;
                              }}
                              onMouseLeave={(e) => {
                                if (!isActive) e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              {article.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Article Content */}
                    <div>
                      {selectedArticle && (
                        <div
                          style={{
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: '12px',
                            padding: '20px',
                          }}
                        >
                          <h2 style={{ fontSize: '15px', fontWeight: 600, color: titleColor, marginBottom: '10px' }}>
                            {selectedArticle.title}
                          </h2>
                          <p style={{ fontSize: '13px', color: titleColor, lineHeight: '1.6', marginBottom: '20px' }}>
                            {selectedArticle.content}
                          </p>

                          {/* Steps */}
                          {selectedArticle.steps.length > 0 && (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '14px' }}>
                                <ListOrdered size={16} style={{ color: iconColor }} />
                                <span style={{ fontSize: '13px', fontWeight: 600, color: titleColor }}>
                                  {t('help.steps')}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                {selectedArticle.steps.map((step, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      borderLeft: `2px solid ${stepBorderColor}`,
                                      paddingLeft: '14px',
                                    }}
                                  >
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                      <span
                                        style={{
                                          flexShrink: 0,
                                          width: '22px',
                                          height: '22px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '999px',
                                          fontSize: '11px',
                                          fontWeight: 600,
                                          color: '#FFFFFF',
                                          background: buttonBg,
                                        }}
                                      >
                                        {index + 1}
                                      </span>
                                      <span style={{ fontSize: '13px', color: titleColor, paddingTop: '1px', flex: 1, lineHeight: '1.5' }}>
                                        {step.description}
                                      </span>
                                    </div>

                                    {step.image && (
                                      <div style={{ marginLeft: '30px', marginBottom: '8px' }}>
                                        <div
                                          style={{
                                            border: `1px solid ${cardBorder}`,
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            background: isDark ? '#1D293D' : '#F8FAFC',
                                          }}
                                        >
                                          <img
                                            src={step.image}
                                            alt={t('help.stepAlt', { step: index + 1 })}
                                            style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                                            onError={(e) => {
                                              const target = e.target as HTMLImageElement;
                                              target.style.display = 'none';
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {step.video && (
                                      <div style={{ marginLeft: '30px' }}>
                                        <div
                                          style={{
                                            aspectRatio: '16/9',
                                            background: isDark ? '#1D293D' : '#F8FAFC',
                                            borderRadius: '8px',
                                            border: `1px solid ${cardBorder}`,
                                            overflow: 'hidden',
                                          }}
                                        >
                                          <iframe
                                            width="100%"
                                            height="100%"
                                            src={step.video.replace('youtu.be/', 'www.youtube.com/embed/').replace('watch?v=', 'embed/').split('&')[0].split('?')[0]}
                                            title={t('help.videoTutorial', { step: index + 1 })}
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            style={{ width: '100%', height: '100%' }}
                                          ></iframe>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Support section intentionally excluded */}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <LegalModal docKey={activeLegalDoc} onClose={() => setActiveLegalDoc(null)} />
      <AboutModal open={showAboutModal} onClose={() => setShowAboutModal(false)} />
    </>
  );
};

export default Footer;
